import { useEditorState } from '@tiptap/react'
import { StrictMode, VFC, MouseEventHandler } from 'react'
import { useTextEditor } from '../../context'
import { MemoizedPopover } from './MemoizedPopover'
import { ControlButtonProps } from './types'

export const ControlButton: VFC<ControlButtonProps> = ({
  onClick,
  isActive: isActiveCommand,
  label,
  icon,
  styleOverrides,
  ...buttonProps
}) => {
  const { editor } = useTextEditor()
  const isActive = useEditorState({
    editor,
    selector: (snapshot) => {
      // Not all buttons have an 'active' state, ignore those
      if (isActiveCommand === undefined) {
        return false
      }
      // If we're given a straight boolean, return that
      if (typeof isActiveCommand === 'boolean') {
        return isActiveCommand
      }
      // Some extensions only provide the second argument, e.g. TextAlign
      if (isActiveCommand.length === 1) {
        return snapshot.editor.isActive(isActiveCommand[0])
      }

      const [name, attributes] = isActiveCommand
      return snapshot.editor.isActive(name, attributes)
    },
  })

  const labelText = typeof label === 'function' ? label({ isActive }) : label

  // Consumer will provide chained action to run on toggle
  const handleToggle: MouseEventHandler<HTMLButtonElement> = async (event) => {
    if (editor && !editor.isDestroyed) {
      const chain = await Promise.resolve(
        onClick(editor.chain().focus(), event)
      )
      chain?.run()
    }
  }

  return (
    <StrictMode>
      <MemoizedPopover label={labelText}>
        <button
          css={styleOverrides}
          {...buttonProps}
          onClick={handleToggle}
          aria-label={labelText}
          aria-pressed={!buttonProps.disabled && isActive}
        >
          {icon}
        </button>
      </MemoizedPopover>
    </StrictMode>
  )
}
